<template>
    <div class="segment-page p-2 mx-2" id="segment-page">
        <h1 class="mb-3">{{ page_name }} {{ page_name === 'Add' ? 'an' : 'the' }} Audience Template</h1>

        <div class="card p-0 mb-4">
            <div class="card-body">
                <div class="row">
                    <div class="col mb-3" :class="{ 'is-invalid': errors.name !== '' }">
                        <label class="form-label">Audience Name</label>
                        <input 
                            type="text" 
                            class="form-control" 
                            name="name"
                            v-model="local_segment.name"
                            :class="{ 'is-invalid': errors.name !== '' }"
                         aria-label="Audience Name">
                        <div class="text-danger invalid-feedback" v-show="errors.name !== ''">
                            {{ errors.name }}
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-4 mb-3">
                        <label class="form-label">Audience Type</label>
                        <select class="form-select" name="type" @change="changeAudienceType()" v-model="local_segment.type" aria-label="Audience Type">
                            <option value="owned">Owned</option>
                            <option value="modeled">Modeled</option>
                        </select>
                    </div>
                    <div class="col-sm-4 mb-3" v-if="local_segment.type === 'owned'">
                        <label class="form-label">Test Against Client</label>
                        <select 
                            class="form-select" 
                            v-model="selected_client" 
                            @change="handleClientChange"
                         aria-label="Test Against Client">
                            <option value="">Select a client...</option>
                            <option 
                                v-for="client in clients" 
                                :key="client.id" 
                                :value="client"
                            >
                                {{ client.name }}
                            </option>
                        </select>
                    </div>
                    <div class="col-sm-4 mb-3" v-if="local_segment.type === 'modeled'">
                        <label class="form-label">Base Audience</label>
                        <select 
                            class="form-select" 
                            name="modeled_segment_id" 
                            v-model="local_segment.modeled_segment_id"
                            :disabled="isEditingModeledSegment"
                         aria-label="Base Audience">
                            <option value="">Select an owned audience...</option>
                            <option v-for="segment in ownedSegments" :value="segment.id">{{segment.name}}</option>
                        </select>
                    </div>
                    <div class="col mb-3" v-if="local_segment.type === 'owned'">
                        <label class="form-label">Data Source Type</label>
                        <select 
                            class="form-select" 
                            v-model="local_segment.source" 
                            @change="handleSourceChange"
                         aria-label="Data Source Type">
                            <option value="constituent">Constituent</option>
                            <option value="subscriber">Subscriber</option>
                            <option value="transformation">Data Set</option>
                        </select>
                    </div>
                    <div 
                        class="col-sm-3 mb-3" 
                        v-show="local_segment.source === 'transformation' && local_segment.type === 'owned'"
                    >
                        <label class="form-label">Data Set</label>
                        <select 
                            class="form-select" 
                            v-model="local_segment.data_source_id"
                         aria-label="Data Set">
                            <option 
                                v-for="trans in transformations" 
                                :key="trans.id" 
                                :value="trans.id"
                            >
                                {{ trans.name }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="row">
                    <div class="col mb-3">
                        <label class="form-label">
                            What would motivate this audience to take action? 
                            <em>(Optional)</em> 
                            <img class="info-icon" src="/img/icons/avid-icons/info.svg" 
                                data-bs-toggle="tooltip" 
                                data-placement="top" 
                                title="By providing a motivation for this audience, you will be able to create content that is more relevant to them." alt=""
                            ></img>
                        </label>
                        <textarea 
                            class="form-control" 
                            v-model="local_segment.value_prop" 
                            rows="3" 
                            placeholder="Enter a couple of sentences that would be helpful to know when drafting copy for this audience"
                        ></textarea>
                    </div>
                </div>

                <div class="row"></div>
                
                <h6 class="mt-4" v-if="local_segment.type === 'owned'">Audience Definition</h6>
                <div class="row" v-if="local_segment.type === 'owned'">
                    <div class="col">
                        <div :class="{ 'is-invalid': errors.value !== '' }">
                            <query-builder 
                                :client="selected_client" 
                                v-model="local_segment.filter" 
                                :include_trans="true"
                                :data_source_id="getDataSourceId()" 
                                :source="getSourceType()" 
                                :in_card="false"
                            />
                        </div>
                        <div class="text-danger invalid-feedback mb-2" v-show="errors.value !== ''">
                            {{ errors.value }}
                        </div>
                    </div>
                </div>
            </div>         
        </div>

        <div class="row mt-2" v-show="form.error">
            <div class="col">
                <div class="alert alert-danger">
                    There was an issue saving your audience. Check the fields above to get more information.
                </div>
            </div>
        </div>
        <div class="mb-4 mt-2">
            <button 
                type="button" 
                class="btn btn-primary btn-lg save-button" 
                :disabled="form.saving"
                @click.prevent="saveSegment"
            >
                <span v-if="form.saving">
                    <div class="spinner-border spinner-border-sm float-left" role="status">
                        <span class="visually-hidden">Saving...</span>
                    </div> 
                    Saving
                </span>
                <span v-else>
                    <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
                </span>
            </button>
        </div>   
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';

export default defineComponent({
    name: 'TransformationSegmentEditor',
    props: {
        segment: {
            type: Object,
            required: true
        },
        clients: {
            type: Array,
            required: true
        },
        transformations: {
            type: Array,
            required: true
        },
        segments: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            form: {
                saving: false,
                error: false
            },
            errors: {
                name: "",
                value: ""
            },
            local_segment: {},
            selected_client: null,
            client: {
                url: 'transformation'
            }
        };
    },
    beforeMount() { 
        if (this.segment && this.segment.id) {
            this.local_segment = { 
                ...this.segment,
                type: this.segment.type || 'owned',
                modeled_segment_id: this.segment.base_segment?.id || null
            };
        } else {
            this.local_segment = {
                type: 'owned',
                source: 'constituent',
                data_source_id: null,
                filter: null,
                name: '',
                value_prop: '',
                modeled_segment_id: null
            };
        }
        if (!this.local_segment.source) {
            this.local_segment.source = 'constituent';
        }
        
        // Set default client if available
        if (this.clients && this.clients.length > 0) {
            this.selected_client = this.clients[0];
            this.client = this.clients[0];
        }
    },
    computed: {
        page_name() {
            if (this.local_segment.id == null || this.local_segment.id <= 0) {
                return 'Add';
            }
            return 'Edit';
        },
        ownedSegments() {
            return this.segments.filter(seg => seg.type === 'owned').sort((a, b) => a.name.localeCompare(b.name));
        },
        isEditingModeledSegment() {
            return this.local_segment.id != null && 
                   this.local_segment.id > 0 && 
                   this.segment && 
                   this.segment.type === 'modeled';
        }
    },
    methods: {
        getSourceType() {
            if (this.local_segment.source === 'constituent' || this.local_segment.source === 'subscriber') {
                return 'data_blend';
            }
            return 'data_set'; 
        },
        getDataSourceId() {
            if (this.local_segment.source === 'constituent' || this.local_segment.source === 'subscriber') {
                for (let i = 0; i < this.selected_client.data_blends.length; i++) {
                    if (this.selected_client.data_blends[i].type === this.local_segment.source) {
                        return this.selected_client.data_blends[i].id;
                    }
                }

                // Find a client that has this data_blend type
                for (let i = 0; i < this.clients.length; i++) {
                    const client = this.clients[i];
                    for (let j = 0; j < client.data_blends.length; j++) {
                        if (client.data_blends[j].type === this.local_segment.source) {
                            this.selected_client = client;
                            return client.data_blends[j].id;
                        }
                    }
                }
                return null;
            } else {
                for (let i = 0; i < this.selected_client.data_sets.length; i++) {
                    if (this.selected_client.data_sets[i].transformation_id && 
                        this.selected_client.data_sets[i].transformation_id === this.local_segment.data_source_id) {
                        return this.selected_client.data_sets[i].id;
                    }
                }
            }
            return null;
        },
        handleSourceChange() {
            if (this.local_segment.source !== 'transformation') {
                this.local_segment.data_source_id = null;
            }
        },
        handleClientChange() {
            if (this.selected_client) {
                this.client = this.selected_client;
            }
        },
        changeAudienceType() {
            if (this.local_segment.type === 'owned') {
                this.local_segment.modeled_segment_id = null;
            } else if (this.local_segment.type === 'modeled') {
                this.local_segment.source = null;
                this.local_segment.data_source_id = null;
                this.local_segment.combo_source = null;
                this.local_segment.filter = null;
            }
        },
        saveSegment() {
            this.form.error = false;

            //Verify they have named the segment
            if (!this.local_segment.name || this.local_segment.name === '') {
                this.errors.name = 'Please enter a name for the audience';
                this.form.error = true;
            } else {
                this.errors.name = "";
            }

            // Validate modeled segment selection
            if (this.local_segment.type === 'modeled' && !this.local_segment.modeled_segment_id) {
                this.errors.value = 'Please select a base audience for modeled segments';
                this.form.error = true;
            } else if (this.local_segment.type === 'owned') {
                this.form.error = !this.filterLogicIsValid(this.local_segment.filter);
            }

            if (this.form.error) {
                return;
            }

            const self = this;
            this.form.saving = true;

            const handleError = (error) => {
                self.form.saving = false;
                self.form.error = true;
                
                let errorMessage = 'An unexpected error occurred while saving the audience.';
                
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    if (error.response.status === 500) {
                        errorMessage = 'A server error occurred. Please try again later.';
                    } else if (error.response.data && error.response.data.message) {
                        errorMessage = error.response.data.message;
                    }
                } else if (error.request) {
                    // The request was made but no response was received
                    errorMessage = 'Network error. Please check your connection and try again.';
                }
                
                Swal.fire({
                    title: "Error!",
                    text: errorMessage,
                    icon: "error",
                    customClass: {
                        popup: 'swal-error'
                    },
                });
                
                setTimeout(() => {
                    self.form.error = false;
                }, 10000);
            };

            const performSave = (segmentData) => {
                if (self.local_segment.type === 'modeled') {
                    // For existing modeled segments, preserve the original data_source_id
                    if (self.local_segment.id != null && self.segment && self.segment.type === 'modeled' && self.segment.data_source_id) {
                        segmentData.data_source_id = self.segment.data_source_id;
                    } else {
                        segmentData.data_source_id = segmentData.modeled_segment_id;
                    }
                    delete segmentData.modeled_segment_id;
                }

                if (self.local_segment.id == null) {
                    return window.axios.post('/api/transformation-segments', segmentData);
                } else {
                    return window.axios.put('/api/transformation-segments/' + self.local_segment.id, segmentData);
                }
            };

            performSave({ ...this.local_segment })
                .then(response => {
                    if (response.status === 200) {
                        self.local_segment = response.data.data;
                        Swal.fire({
                            title: "Success!",
                            html: "The audience name <strong>" + response.data.data.name + "</strong> was successfully " + (self.local_segment.id ? "updated" : "created") + ".",
                            icon: "success",
                            customClass: {
                                popup: 'swal-success'
                            },
                        }).then((result) => {
                            if (result.isConfirmed) {
                                window.location.href = '/admin/transformation_segments';
                            }
                        });
                    }
                })
                .catch(error => {
                    // Restore modeled_segment_id if save fails
                    if (self.local_segment.type === 'modeled') {
                        self.local_segment.modeled_segment_id = self.local_segment.data_source_id;
                    }
                    handleError(error);
                })
                .finally(() => {
                    self.form.saving = false;
                });
        },
        filterLogicIsValid(filter) {
            let is_valid = true;

            //First reset the errors
            this.errors.value = "";

            //If they didn't enter any logic
            if (filter === "" || filter === null) {
                this.errors.value = "Please select a column and value to use with the filter.";
                is_valid = false;
            } else {
                const logic_units = filter.logic_units;
                for (let i = 0; i < logic_units.length; i++) {
                    if (logic_units[i].db_column === "" || 
                        logic_units[i].db_column === null || 
                        logic_units[i].db_column.value === "" ||
                        logic_units[i].operator === undefined || 
                        (typeof logic_units[i].operator === 'string' && logic_units[i].operator === "") ||
                        (logic_units[i].operator instanceof Object && logic_units[i].operator.value === "") ||
                        (logic_units[i].value === "" && logic_units[i].operator.value.indexOf("NULL") === -1)) {
                        is_valid = false;
                        this.errors.value = "Please enter values for all of the inputs above.";
                    }
                }
            }
            return is_valid;
        }
    }
});
</script> 